"use client";

import React, { useEffect, useRef, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * GastronomyWebsite — Section 3: Engine-Animation
 * Drop-in für: src/pages/GastronomyWebsite/sections/EngineAnimation.tsx
 *
 * Selbe Engine-Animation wie auf der Homepage, aber Marco/Restaurant
 * startet den Cycle (Niche-spezifisch).
 */

type Persona = {
  initial: string;
  name: string;
  role: string;
  prompt: string;
  url: string;
  image: string;
  seconds: number;
};
const PERSONAS: Persona[] = [{
  initial: "M",
  name: "Marco Rossi",
  role: "Restaurant-Inhaber, Zürich",
  prompt: "Italienisches Restaurant in Zürich. Hausgemachte Pasta, Pizza aus dem Steinofen, gemütliches Ambiente.",
  url: "zurichresto.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/restaurant.png-scaled.jpg?v=j1jzzezydc",
  seconds: 47
}, {
  initial: "L",
  name: "Lara Brunner",
  role: "Webdesignerin · Freelancer",
  prompt: "Coiffeur-Salon in Bern. Klassische Schnitte, Premium-Erlebnis, online buchbar — für meinen Kunden.",
  url: "mariahairsalon.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Hair-Salo-scaled.png?v=co1azvw1u8q",
  seconds: 52
}, {
  initial: "S",
  name: "Sarah Steiner",
  role: "Inhaberin Web-Agentur, Winterthur",
  prompt: "Maler-Betrieb in Bern. Innen- und Aussenarbeiten, Vorher/Nachher-Galerie — für meinen Kunden.",
  url: "colorcraft-bern.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Moler-scaled.png?v=a6tiktpldx",
  seconds: 41
}, {
  initial: "T",
  name: "Tobias Frey",
  role: "Geschäftsführer Immobilien, Lausanne",
  prompt: "Immobilienmakler am Genfersee. Exklusive Objekte, mehrsprachig, Premium-Look mit grosser Galerie.",
  url: "vista-properties.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Real-Estate-scaled.png?v=pyzxuisv23",
  seconds: 58
}];
const ENGINE_PHASES = ["analysiert Branche...", "wählt passendes Layout...", "generiert Texte & Bilder...", "optimiert für Google...", "publiziert auf Google Cloud..."];
const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
export default function EngineAnimation() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [idx, setIdx] = useState(0);
  const [persona, setPersona] = useState<Persona>(PERSONAS[0]);
  const [personaVisible, setPersonaVisible] = useState(true);
  const [avatarVisible, setAvatarVisible] = useState(true);
  const [typedText, setTypedText] = useState("");
  const [sendStatus, setSendStatus] = useState<"bereit" | "tippt..." | "gesendet" | "erstellt">("bereit");
  const [engineStatus, setEngineStatus] = useState("wartet auf Eingabe...");
  const [engineStatusColor, setEngineStatusColor] = useState<"gray" | "primary" | "green">("gray");
  const [engineStatusVisible, setEngineStatusVisible] = useState(true);
  const [outputUrl, setOutputUrl] = useState(PERSONAS[0].url);
  const [outputUrlVisible, setOutputUrlVisible] = useState(true);
  const [outputImage, setOutputImage] = useState(PERSONAS[0].image);
  const [outputImageOpacity, setOutputImageOpacity] = useState(0.18);
  const [outputTime, setOutputTime] = useState<string | number>("—");
  const [outputTimeVisible, setOutputTimeVisible] = useState(true);
  const [liveBadgeVisible, setLiveBadgeVisible] = useState(false);
  const [materializeKey, setMaterializeKey] = useState(0);
  const cancelledRef = useRef(false);
  useEffect(() => {
    PERSONAS.forEach(p => {
      const img = new Image();
      img.src = p.image;
    });
  }, []);
  useEffect(() => {
    cancelledRef.current = false;
    const run = async () => {
      const p = PERSONAS[idx];
      setAvatarVisible(false);
      await sleep(280);
      if (cancelledRef.current) return;
      setPersona(p);
      setAvatarVisible(true);
      setPersonaVisible(false);
      await sleep(200);
      if (cancelledRef.current) return;
      setPersonaVisible(true);
      setSendStatus("tippt...");
      setEngineStatusVisible(false);
      await sleep(160);
      setEngineStatus("wartet auf Eingabe...");
      setEngineStatusColor("gray");
      setEngineStatusVisible(true);
      setOutputImageOpacity(0.18);
      setLiveBadgeVisible(false);
      setTypedText("");
      for (let i = 0; i < p.prompt.length; i++) {
        if (cancelledRef.current) return;
        setTypedText(p.prompt.slice(0, i + 1));
        const c = p.prompt[i];
        await sleep(c === "." || c === "," || c === "!" ? 180 : 24);
      }
      await sleep(450);
      if (cancelledRef.current) return;
      setSendStatus("gesendet");
      setEngineStatusColor("primary");
      for (const phase of ENGINE_PHASES) {
        if (cancelledRef.current) return;
        setEngineStatusVisible(false);
        await sleep(140);
        setEngineStatus(phase);
        setEngineStatusVisible(true);
        await sleep(480);
      }
      setOutputUrlVisible(false);
      await sleep(160);
      if (cancelledRef.current) return;
      setOutputUrl(p.url);
      setOutputUrlVisible(true);
      setOutputImage(p.image);
      setOutputImageOpacity(1);
      setMaterializeKey(k => k + 1);
      setOutputTimeVisible(false);
      await sleep(120);
      if (cancelledRef.current) return;
      setOutputTime(p.seconds);
      setOutputTimeVisible(true);
      await sleep(300);
      if (cancelledRef.current) return;
      setLiveBadgeVisible(true);
      setEngineStatusVisible(false);
      await sleep(120);
      setEngineStatus("fertig.");
      setEngineStatusColor("green");
      setEngineStatusVisible(true);
      setSendStatus("erstellt");
      await sleep(2800);
      if (cancelledRef.current) return;
      setIdx(prev => (prev + 1) % PERSONAS.length);
    };
    const t = setTimeout(run, 100);
    return () => {
      cancelledRef.current = true;
      clearTimeout(t);
    };
  }, [idx]);
  const statusColorClass = engineStatusColor === "primary" ? "text-primary" : engineStatusColor === "green" ? "text-green-600" : "text-muted-foreground";
  return <section className="relative bg-background py-20 md:py-28 overflow-hidden">
      <style>{`
        @keyframes ea-pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
        .ea-pulse { animation: ea-pulse 1.6s ease-in-out infinite; }
        @keyframes ea-core-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(232,107,42,0.45); }
          50%      { transform: scale(1.06); box-shadow: 0 0 0 22px rgba(232,107,42,0); }
        }
        .ea-core-pulse { animation: ea-core-pulse 2.2s ease-in-out infinite; }
        @keyframes ea-spin-slow { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes ea-spin-rev  { from { transform: rotate(360deg); } to { transform: rotate(0); } }
        .ea-spin-slow { animation: ea-spin-slow 14s linear infinite; }
        .ea-spin-rev  { animation: ea-spin-rev  20s linear infinite; }
        @keyframes ea-materialize {
          0%   { opacity: 0; transform: scale(.94); filter: blur(14px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        .ea-materialize { animation: ea-materialize 1s cubic-bezier(0.22,1,0.36,1) both; }
        @keyframes ea-caret { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        .ea-caret { animation: ea-caret 1s steps(1) infinite; display: inline-block; width: 2px; height: 1em; vertical-align: -2px; background: currentColor; margin-left: 2px; }
        .ea-conn-h { height: 2px; background-image: linear-gradient(to right, hsl(16,88%,54%) 50%, transparent 50%); background-size: 14px 2px; animation: ea-flow-x 0.6s linear infinite; }
        @keyframes ea-flow-x { from { background-position: 0 0; } to { background-position: 14px 0; } }
        .ea-conn-v { width: 2px; background-image: linear-gradient(to bottom, hsl(16,88%,54%) 50%, transparent 50%); background-size: 2px 14px; animation: ea-flow-y 0.6s linear infinite; }
        @keyframes ea-flow-y { from { background-position: 0 0; } to { background-position: 0 14px; } }
      `}</style>

      <div ref={sectionRef} className={`relative max-w-6xl mx-auto px-4 transition-all duration-1000 ease-out ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
            <span className="w-2 h-2 rounded-full bg-primary ea-pulse" />
            So funktioniert's
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Vom <span className="text-primary">Satz</span> zur{" "}
            <span className="text-primary">Restaurant-Website</span>.
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Beschreiben Sie Ihr Restaurant in einem Satz — die
            PAGEMAKER24-Engine baut daraus Ihre fertige Gastro-Website.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-5 items-stretch">
          {/* STAGE 1 — INPUT */}
          <div className="relative">
            <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase mb-3 text-center md:text-left">
              1 · Sie beschreiben
            </div>
            <div className="bg-background border border-border rounded-2xl p-5 md:p-6 shadow-sm h-full flex flex-col">
              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-border/50">
                <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg shrink-0 transition-opacity duration-300" style={{
                opacity: avatarVisible ? 1 : 0
              }}>
                  {persona.initial}
                </div>
                <div className="min-w-0 transition-opacity duration-300" style={{
                opacity: personaVisible ? 1 : 0
              }}>
                  <div className="text-sm font-semibold text-foreground truncate">
                    {persona.name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {persona.role}
                  </div>
                </div>
              </div>
              <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                Prompt
              </div>
              <div className="bg-secondary border border-border rounded-xl p-4 text-sm text-foreground leading-relaxed flex-grow min-h-[124px]">
                <span>{typedText}</span>
                <span className="ea-caret text-primary" />
              </div>
              <div className="mt-4 flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
                <span>{sendStatus}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>

          {/* STAGE 2 — ENGINE */}
          <div className="relative flex flex-col items-center justify-center md:px-2">
            <div className="md:hidden flex flex-col items-center mb-2">
              <div className="ea-conn-v h-8" />
            </div>
            <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase mb-4 text-center hidden md:block">
              2 · Engine
            </div>
            <div className="relative w-36 h-36 md:w-44 md:h-44 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary/30 ea-spin-slow" />
              <div className="absolute inset-3 rounded-full border-2 border-dotted border-primary/30 ea-spin-rev" />
              <div className="absolute inset-5 rounded-full bg-primary/10 blur-md" />
              <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary text-white flex items-center justify-center shadow-xl shadow-primary/30 ea-core-pulse">
                <Sparkles className="w-9 h-9 md:w-11 md:h-11" strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-5 text-center">
              <div className="text-sm font-bold text-foreground tracking-tight">
                PAGEMAKER24-Engine
              </div>
              <div className={`text-xs font-medium mt-1 min-h-[1.25rem] transition-opacity duration-300 ${statusColorClass}`} style={{
              opacity: engineStatusVisible ? 1 : 0
            }}>
                {engineStatus}
              </div>
            </div>
            <div className="md:hidden flex flex-col items-center mt-2">
              <div className="ea-conn-v h-8" />
            </div>
            <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full w-5 ea-conn-h" />
            <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-5 ea-conn-h" />
          </div>

          {/* STAGE 3 — OUTPUT */}
          <div className="relative">
            <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase mb-3 text-center md:text-right">
              3 · Fertige Website
            </div>
            <div className="bg-background border border-border rounded-2xl p-5 md:p-6 shadow-sm h-full flex flex-col">
              <div className="flex items-center gap-1.5 mb-3">
                <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-300" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
                <div className="ml-auto text-[10px] text-muted-foreground font-medium truncate transition-opacity duration-300" style={{
                opacity: outputUrlVisible ? 1 : 0
              }}>
                  {outputUrl}
                </div>
              </div>
              <div className="relative flex-grow rounded-lg overflow-hidden border border-border bg-secondary aspect-[16/11]">
                <img key={materializeKey} src={outputImage} alt="Generierte Website" className="w-full h-full object-cover object-top ea-materialize" style={{
                opacity: outputImageOpacity,
                transition: "opacity 0.4s ease"
              }} />
                <div className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-[10px] font-medium px-2 py-1 rounded-full border border-green-200 transition-opacity duration-500" style={{
                opacity: liveBadgeVisible ? 1 : 0
              }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 ea-pulse" />
                  Live
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">erstellt in</span>
                <span className="font-bold text-foreground">
                  <span className="inline-block transition-opacity duration-300" style={{
                  opacity: outputTimeVisible ? 1 : 0
                }}>
                    {outputTime}
                  </span>{" "}
                  Sekunden
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-10">
          {PERSONAS.map((_, i) => <span key={i} className={i === idx ? "w-6 h-1.5 rounded-full bg-primary transition-all duration-300" : "w-1.5 h-1.5 rounded-full bg-muted transition-all duration-300"} />)}
        </div>
      </div>
    </section>;
}